import { prisma, CheckInOutType, OrderStatus } from "client";
export interface CheckInOutLog {
  id: string;
  orderNo: number;
  orderItemNo: string;
  ticketType: string;
  accessPoint: string;
  checkInRuleName: string;
  accessType: string;
  accessTime: Date;
  statusMessage: string;
  message: string | null;
  agentName: string;
  createdOn: Date;
  aclId: string;
  isFailure?: boolean;
  failureReason?: string;
}

const OBJECT_ID_REGEX = /^[a-f\d]{24}$/i;

async function resolveSearchIds(eventId: string, search: string) {
  const trimmed = search.trim();
  if (!trimmed) {
    return {
      orderItemIds: [] as string[],
      createdByIds: [] as string[],
      zoneIds: [] as string[],
    };
  }

  const searchNumber = Number.isNaN(Number(trimmed)) ? null : Number(trimmed);
  const isObjectId = OBJECT_ID_REGEX.test(trimmed);

  const [users, zoneTranslations, bookableItemTranslations, profiles, matchingOrders] =
    await Promise.all([
      prisma.user.findMany({
        where: {
          name: {
            contains: trimmed,
            mode: "insensitive",
          },
        },
        select: { id: true },
      }),
      prisma.zoneTranslations.findMany({
        where: {
          name: {
            contains: trimmed,
            mode: "insensitive",
          },
        },
        select: { zoneId: true },
      }),
      prisma.bookableItemTranslations.findMany({
        where: {
          name: {
            contains: trimmed,
            mode: "insensitive",
          },
        },
        select: { bookableItemId: true },
      }),
      prisma.profile.findMany({
        where: {
          eventId,
          name: {
            contains: trimmed,
            mode: "insensitive",
          },
        },
        select: { id: true },
      }),
      searchNumber !== null || isObjectId
        ? prisma.order.findMany({
            where: {
              eventId,
              status: OrderStatus.COMPLETED,
              OR: [
                ...(searchNumber !== null ? [{ number: { equals: searchNumber } }] : []),
                ...(isObjectId ? [{ id: trimmed }] : []),
              ],
            },
            select: { id: true },
          })
        : Promise.resolve([] as { id: string }[]),
    ]);

  const bookableItemIds = [
    ...new Set(bookableItemTranslations.map((translation) => translation.bookableItemId)),
  ];
  const profileIds = profiles.map((profile) => profile.id);

  const [tickets, invitationAttendees] = await Promise.all([
    bookableItemIds.length
      ? prisma.ticket.findMany({
          where: {
            bookableItemId: {
              in: bookableItemIds,
            },
          },
          select: { id: true },
        })
      : Promise.resolve([] as { id: string }[]),
    profileIds.length
      ? prisma.invitationAttendee.findMany({
          where: {
            contactId: {
              in: profileIds,
            },
          },
          select: { id: true },
        })
      : Promise.resolve([] as { id: string }[]),
  ]);

  const ticketIds = tickets.map((ticket) => ticket.id);
  const invitationAttendeeIds = invitationAttendees.map(
    (invitationAttendee) => invitationAttendee.id
  );
  const orderIds = matchingOrders.map((order) => order.id);

  const orderItemOrFilters: any[] = [
    ...(isObjectId ? [{ id: trimmed }] : []),
    ...(orderIds.length
      ? [
          {
            orderId: {
              in: orderIds,
            },
          },
        ]
      : []),
    ...(ticketIds.length
      ? [
          {
            ticketId: {
              in: ticketIds,
            },
          },
        ]
      : []),
    ...(invitationAttendeeIds.length
      ? [
          {
            invitationAttendeeId: {
              in: invitationAttendeeIds,
            },
          },
        ]
      : []),
  ];

  const orderItemIds = orderItemOrFilters.length
    ? (
        await prisma.orderItem.findMany({
          where: {
            order: {
              eventId,
              status: OrderStatus.COMPLETED,
            },
            OR: orderItemOrFilters,
          },
          select: { id: true },
        })
      ).map((orderItem) => orderItem.id)
    : [];

  return {
    orderItemIds: [...new Set(orderItemIds)],
    createdByIds: users.map((user) => user.id),
    zoneIds: [...new Set(zoneTranslations.map((translation) => translation.zoneId))],
  };
}

function buildScalarSearchOrConditions(
  search: string,
  ids: {
    orderItemIds: string[];
    createdByIds: string[];
    zoneIds: string[];
  },
  includeFailureReason: boolean
): any[] {
  const trimmed = search.trim();
  if (!trimmed) return [];

  const or: any[] = [];

  if (ids.orderItemIds.length) {
    or.push({
      orderItemId: {
        in: ids.orderItemIds,
      },
    });
  }

  if (ids.createdByIds.length) {
    or.push({
      createdById: {
        in: ids.createdByIds,
      },
    });
  }

  if (ids.zoneIds.length) {
    or.push({
      zoneId: {
        in: ids.zoneIds,
      },
    });
  }

  if (includeFailureReason) {
    or.push({
      reason: {
        contains: trimmed,
        mode: "insensitive",
      },
    });
  }

  return or;
}

export const getCheckInOutLogs = async (
  _userId: string,
  eventId: string,
  page: number = 1,
  limit: number = 10,
  search?: string,
  zoneId?: string,
  status?: string
) => {
  try {
    const skip = (page - 1) * limit;
    const trimmedSearch = search?.trim();
    const searchIds = trimmedSearch
      ? await resolveSearchIds(eventId, trimmedSearch)
      : null;
    const successSearchOr =
      trimmedSearch && searchIds
        ? buildScalarSearchOrConditions(trimmedSearch, searchIds, false)
        : [];
    const failureSearchOr =
      trimmedSearch && searchIds
        ? buildScalarSearchOrConditions(trimmedSearch, searchIds, true)
        : [];

    const successBase = {
      orderItem: {
        order: {
          eventId,
          status: OrderStatus.COMPLETED,
        },
      },
    };

    const where: any =
      trimmedSearch
        ? {
            AND: [
              {
                OR: successSearchOr.length > 0 ? successSearchOr : [{ id: { in: [] } }],
              },
              successBase,
              ...(zoneId ? [{ zoneId }] : []),
              ...(status ? [{ type: status as CheckInOutType }] : []),
            ],
          }
        : {
            ...successBase,
            ...(zoneId ? { zoneId } : {}),
            ...(status ? { type: status as CheckInOutType } : {}),
          };

    const failureBase = {
      eventId,
      orderItem: {
        order: {
          status: OrderStatus.COMPLETED,
        },
      },
    };

    const failureWhere: any =
      trimmedSearch
        ? {
            AND: [
              {
                OR: failureSearchOr.length > 0 ? failureSearchOr : [{ id: { in: [] } }],
              },
              failureBase,
              ...(zoneId ? [{ zoneId }] : []),
              ...(status ? [{ type: status as CheckInOutType }] : []),
            ],
          }
        : {
            ...failureBase,
            ...(zoneId ? { zoneId } : {}),
            ...(status ? { type: status as CheckInOutType } : {}),
          };

    // Get total counts
    const [successCount, failureCount] = await Promise.all([
      prisma.checkInOut.count({ where }),
      prisma.checkInOutFailure.count({ where: failureWhere }),
    ]);
    const total = successCount + failureCount;

    // Need up to (skip + limit) rows from each stream so that sorting the union
    // reproduces the global time order for slice [skip, skip + limit).
    const mergeWindow = skip + limit;

    // Get success logs
    const successLogs = await prisma.checkInOut.findMany({
      where,
      skip: 0,
      take: mergeWindow,
      orderBy: {
        time: "desc",
      },
      include: {
        orderItem: {
          include: {
            order: {
              select: {
                number: true,
              },
            },
            ticket: {
              include: {
                bookableItem: {
                  include: {
                    translations: {
                      take: 1,
                    },
                  },
                },
                category: {
                  select: {
                    name: true,
                  },
                },
              },
            },
          },
        },
        zone: {
          include: {
            translations: {
              take: 1,
            },
            checkInRules: {
              select: { name: true },
            },
          },
        },
        createdBy: {
          select: {
            name: true,
            id: true,
          },
        },
      },
    });

    // Get failure logs
    const failureLogs = await prisma.checkInOutFailure.findMany({
      where: failureWhere,
      skip: 0,
      take: mergeWindow,
      orderBy: {
        createdAt: "desc",
      },
      include: {
        orderItem: {
          include: {
            order: {
              select: {
                number: true,
              },
            },
            ticket: {
              include: {
                bookableItem: {
                  include: {
                    translations: {
                      take: 1,
                    },
                  },
                },
                category: {
                  select: {
                    name: true,
                  },
                },
              },
            },
          },
        },
        zone: {
          include: {
            translations: {
              take: 1,
            },
          },
        },
        createdBy: {
          select: {
            name: true,
            id: true,
          },
        },
      },
    });

    // Transform success logs
    const transformedSuccessLogs: CheckInOutLog[] = successLogs.map((log) => ({
      id: log.id,
      orderNo: log.orderItem.order.number,
      orderItemNo: log.orderItem.id,
      ticketType:
        log.orderItem.ticket?.bookableItem?.translations?.[0]?.name ||
        log.orderItem.ticket?.category?.name ||
        "-",
      accessPoint: log.zone?.translations?.[0]?.name || "-",
      checkInRuleName: log.zone?.checkInRules?.[0]?.name || "-",
      accessType:
        log.type === CheckInOutType.CHECK_IN ? "Check In" : "Check Out",
      accessTime: log.time,
      statusMessage: "Success",
      message: log.message || null,
      agentName: log.createdBy?.name || "-",
      createdOn: log.createdAt,
      aclId: log.id,
      isFailure: false,
    }));

    // Transform failure logs
    const transformedFailureLogs: CheckInOutLog[] = failureLogs.map((log) => ({
      id: log.id,
      orderNo: log.orderItem.order.number,
      orderItemNo: log.orderItem.id,
      ticketType:
        log.orderItem.ticket?.bookableItem?.translations?.[0]?.name ||
        log.orderItem.ticket?.category?.name ||
        "-",
      accessPoint: log.zone?.translations?.[0]?.name || "-",
      checkInRuleName: "-",
      accessType:
        log.type === CheckInOutType.CHECK_IN ? "Check In" : "Check Out",
      accessTime: log.createdAt, // Use createdAt for failures
      statusMessage: "Failed",
      message: log.reason,
      agentName: log.createdBy?.name || "-",
      createdOn: log.createdAt,
      aclId: log.id,
      isFailure: true,
      failureReason: log.reason,
    }));

    // Combine and sort by time (most recent first)
    const allLogs = [...transformedSuccessLogs, ...transformedFailureLogs].sort(
      (a, b) => b.accessTime.getTime() - a.accessTime.getTime()
    );

    // Apply pagination to combined results
    const paginatedLogs = allLogs.slice(skip, skip + limit);

    return {
      data: paginatedLogs,
      pagination: {
        total,
        page,
        limit,
        hasNextPage: total > skip + limit,
        totalPages: Math.ceil(total / limit),
      },
    };
  } catch (error) {
    console.error("Error fetching check-in-out logs:", error);
    throw error;
  }
};
